import { useEffect, useState } from 'react';

import { Box, Button, IconButton } from '@mui/material';
import {
  Link as RouterLink,
  // useLocation,
  useNavigate
} from 'react-router-dom';
import MenuItem from '@mui/material/MenuItem';
import jwt_decode from 'jwt-decode';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import Cookies from 'universal-cookie';

import Select from 'components/common/Select';
import CustomLink from 'components/CustomLink';
import { currentRole, useSetCurrentRole } from 'pages/Login/state';
import { jwtData } from 'pages/OnBoarding/state';
import LogoLight from 'static/logos/Logo.svg?react';
import api from 'utils/api';
import { useConstants, constantsAtom, useRefreshTokens } from 'utils/hooks';
import useWidth from 'utils/useWidth';
import { computedNavLinks } from './state';

// =============================================================================
const appbar_style = {
  display: 'flex',
  height: 94.5,
  alignItems: 'center'
};

const logo_style = {
  width: 116
};

// =============================================================================
interface LogoProps {
  variant: string | undefined;
  myCurrentRole: any;
}

const Logo = (props: LogoProps) => {
  const { CONSTANTS } = useConstants();

  function getLogo() {
    return <LogoLight style={logo_style} />;
  }

  let iconPath = '/';

  if (props.myCurrentRole === CONSTANTS.ROLE.ROLE_DEVELOPER_ADMIN) {
    iconPath = '/owners';
  } else if (props.myCurrentRole === CONSTANTS.ROLE.ROLE_DEVELOPER) {
    iconPath = '/instrument-on-boarding';
  }

  return (
    <IconButton
      component={RouterLink}
      disableRipple
      to={iconPath}
      key={props.myCurrentRole}
    >
      {getLogo()}
    </IconButton>
  );
};

// =============================================================================
interface AppbarProps {
  variant?: 'transparent' | 'admin' | 'default';
}

export default function Appbar(props: AppbarProps) {
  useRefreshTokens();
  const { CONSTANTS } = useConstants();

  const width = useWidth();
  const cookies = new Cookies();
  const myCurrentRole = useRecoilValue(currentRole);
  const setCurrentRole = useSetCurrentRole();
  const [currentMenu, setCurrentMenu] = useState(location.pathname);
  const tokenData: any = useRecoilValue(jwtData);
  const setJwtData = useSetRecoilState(jwtData);

  const setConstantsAtom = useSetRecoilState(constantsAtom);
  const navLinks = useRecoilValue(computedNavLinks);
  const navigate = useNavigate();

  const tokens = cookies.get('admin-tokens');
  const decoded: any = jwt_decode(tokens.accessToken);

  useEffect(() => {
    // TODO: we need a way to get constants from api
    // before calling any other api, as api's are
    // dependent on constants
    const myDefaultRoles = {
      ROLE_USER: 'ROLE_USER',
      ROLE_ADMIN: 'ROLE_ADMIN',
      ROLE_SUB_ADMIN: 'ROLE_SUB_ADMIN',
      ROLE_BUOY_MAINTAINER: 'ROLE_BUOY_MAINTAINER',
      ROLE_DEVELOPER_ADMIN: 'ROLE_DEVELOPER_ADMIN',
      ROLE_DEVELOPER: 'ROLE_DEVELOPER'
    };
    const filteredRoles = decoded.roles.filter(
      (r: any) => r !== myDefaultRoles.ROLE_USER
    );
    let currRole = filteredRoles[0];
    if (filteredRoles.some((role: any) => role === myDefaultRoles.ROLE_ADMIN)) {
      currRole = myDefaultRoles.ROLE_ADMIN;
    } else if (
      filteredRoles.some((role: any) => role === myDefaultRoles.ROLE_SUB_ADMIN)
    ) {
      currRole = myDefaultRoles.ROLE_SUB_ADMIN;
    } else if (
      filteredRoles.some(
        (role: any) => role === myDefaultRoles.ROLE_BUOY_MAINTAINER
      )
    ) {
      currRole = myDefaultRoles.ROLE_BUOY_MAINTAINER;
    }
    setCurrentRole(currRole);
    handleRole(currRole);
    setConstantsAtom(CONSTANTS);
  }, [CONSTANTS]);

  useEffect(() => {
    if (!tokenData) {
      setJwtData(decoded);
    }
  }, []);

  useEffect(() => {
    setCurrentMenu(location.pathname);
  }, [location.pathname]);

  const handleClick = async () => {
    try {
      await api.post('/api/logoutUser');
      cookies.remove('admin-tokens');
      window.sessionStorage.removeItem('currentRole');
      navigate('/login');
    } catch (err: any) {
      console.error(err);
    }
  };

  function getPadding() {
    switch (width) {
      case 'tablet':
        return '0 48px';
      case 'desktop':
        return '0 96px';
      case 'mobile':
        return '0 24px';
      default:
        return '0 144px';
    }
  }

  const backgroundColor = () => {
    return 'blue.900';
  };

  const handleRole = (role: string) => {
    setCurrentRole(role);

    let rolePath = '/';

    if (role === CONSTANTS.ROLE.ROLE_DEVELOPER_ADMIN) {
      rolePath = '/owners';
    } else if (role === CONSTANTS.ROLE.ROLE_DEVELOPER) {
      rolePath = '/instrument-on-boarding';
    }
    navigate(rolePath);
  };

  const handleMenu = (menuItem: string) => {
    setCurrentMenu(menuItem);
  };

  function getMargin() {
    switch (width) {
      case 'tablet':
        return '0 0 0 16px';
      case 'laptop':
        return '0 0 0 24px';
      default:
        return '0 0 0 48px';
    }
  }

  return (
    <Box
      sx={{
        ...appbar_style,
        backgroundColor: backgroundColor(),
        padding: getPadding()
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Logo variant={props.variant} myCurrentRole={myCurrentRole} />

        <div className='app-bar menu'>
          <Select
            label='Menu'
            selected={currentMenu}
            menuProps={{
              PopoverClasses: {
                root: 'menu-list-root'
              }
            }}
            onChange={handleMenu}
            key={currentMenu}
          >
            {navLinks.map(({ label, link }, index) => {
              return (
                <MenuItem key={index} value={link}>
                  <CustomLink
                    key={index}
                    to={link}
                    variant='body_large_strong'
                    margin={getMargin()}
                  >
                    {label}
                  </CustomLink>
                </MenuItem>
              );
            })}
          </Select>
        </div>
      </div>
      <div style={{ flex: 1 }} />
      {decoded && decoded.roles.length && (
        <div className='app-bar'>
          <Select
            label='Current Role'
            selected={myCurrentRole}
            onChange={handleRole}
            key={myCurrentRole}
          >
            {decoded.roles
              .filter((role: string) => role !== CONSTANTS.ROLE.ROLE_USER)
              .map((role: string, i: number) => {
                const roleStr = role.split('_').splice(1).join(' ');
                return (
                  <MenuItem key={i} value={role}>
                    {roleStr}
                  </MenuItem>
                );
              })}
          </Select>
        </div>
      )}
      <Box sx={{ ml: 2 }}>
        <Button
          variant='primary'
          color='light'
          component={RouterLink}
          onClick={handleClick}
          to='/login'
        >
          Log out
        </Button>
      </Box>
    </Box>
  );
}
